'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { SvgAiSpark } from '@/app/_components/AiAgentBanner';
import { useSmoothStreamingText } from '../_hooks/useSmoothStreamingText';
import { AI_SPARK_LAYOUT_ID } from './ChatGeneratingIndicator';
import styles from './IntroMessage.module.scss';
const INTRO_DELAY_MS = 300;
const CHAR_STEP_MS = 35;
interface IntroMessageProps {
  userName: string;
  isGenerating?: boolean;
  isEmptyState?: boolean;
  onTypingComplete: () => void;
}
export const IntroMessage = t0 => {
  const $ = _c(42);
  const {
    userName,
    isGenerating: t1,
    isEmptyState: t2,
    onTypingComplete
  } = t0;
  const isGenerating = t1 === undefined ? false : t1;
  const isEmptyState = t2 === undefined ? false : t2;
  const fullText = `${userName}님께\n딱 맞는 정책, 웰로 AI가 찾아드려요!`;
  const [target, setTarget] = useState("");
  const [hasReleasedIcon, setHasReleasedIcon] = useState(false);
  const hasFiredRef = useRef(false);
  let t3;
  let t4;
  if ($[0] !== fullText) {
    t3 = () => {
      const id = window.setTimeout(() => setTarget(fullText), INTRO_DELAY_MS);
      return () => window.clearTimeout(id);
    };
    t4 = [fullText];
    $[0] = fullText;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      stepMs: CHAR_STEP_MS,
      maxCharsPerStep: 1
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const displayedText = useSmoothStreamingText(target, true, t5);
  const isComplete = !!target && displayedText === target;
  let t6;
  let t7;
  if ($[4] !== isComplete || $[5] !== onTypingComplete) {
    t6 = () => {
      if (isComplete && !hasFiredRef.current) {
        hasFiredRef.current = true;
        onTypingComplete();
      }
    };
    t7 = [isComplete, onTypingComplete];
    $[4] = isComplete;
    $[5] = onTypingComplete;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  if (isGenerating && !hasReleasedIcon) {
    setHasReleasedIcon(true);
  }
  const shouldShowIntroIcon = !hasReleasedIcon && !isGenerating;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t8;
  let t9;
  if ($[8] !== displayedText || $[9] !== fullText || $[10] !== isEmptyState || $[11] !== shouldShowIntroIcon) {
    const lines = displayedText.split("\n");
    const hasMultipleLines = lines.length > 1;
    const t16 = isEmptyState ? "flex-1" : "h-fit";
    if ($[20] !== t16) {
      t14 = cn("flex w-full flex-col items-center justify-center gap-16 px-20 pb-20 pt-24", t16);
      $[20] = t16;
      $[21] = t14;
    } else {
      t14 = $[21];
    }
    if ($[22] !== isEmptyState) {
      t15 = isEmptyState ? {
        paddingBottom: "var(--wello-agent-composer-h, 160px)"
      } : undefined;
      $[22] = isEmptyState;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    t11 = "relative isolate flex flex-col items-center gap-16";
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div aria-hidden={true} className={styles.ambientGlow} />;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t17;
    if ($[25] !== shouldShowIntroIcon) {
      t17 = shouldShowIntroIcon && <motion.div className="flex size-36 items-center justify-center" layoutId={AI_SPARK_LAYOUT_ID} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
        layout: {
          duration: 0.9,
          ease: [0.22, 1, 0.36, 1]
        }
      }}><SvgAiSpark className="size-36" isAnimated={false} /></motion.div>;
      $[25] = shouldShowIntroIcon;
      $[26] = t17;
    } else {
      t17 = $[26];
    }
    if ($[27] !== t17) {
      t13 = <div className="flex h-36 items-center justify-center">{t17}</div>;
      $[27] = t17;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    t8 = fullText;
    t9 = "whitespace-pre-wrap text-center leading-[1.3] text-Gray-900 typo-h7";
    t10 = lines.map((line, lineIndex) => {
      const isLastLine = lineIndex === lines.length - 1;
      return <span key={lineIndex} className={cn(isLastLine && hasMultipleLines && styles.gradientLine)}>{line.split("").map((char, charIndex) => <motion.span key={`${lineIndex}-${charIndex}-${char}`} animate={{
          opacity: 1
        }} aria-hidden={true} initial={{
          opacity: 0
        }} transition={{
          duration: 0.15,
          ease: "easeOut"
        }}>{char}</motion.span>)}{!isLastLine && <br aria-hidden={true} />}</span>;
    });
    $[8] = displayedText;
    $[9] = fullText;
    $[10] = isEmptyState;
    $[11] = shouldShowIntroIcon;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
    $[17] = t15;
    $[18] = t8;
    $[19] = t9;
  } else {
    t10 = $[12];
    t11 = $[13];
    t12 = $[14];
    t13 = $[15];
    t14 = $[16];
    t15 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t16;
  if ($[29] !== t10 || $[30] !== t8 || $[31] !== t9) {
    t16 = <h2 aria-label={t8} className={t9}>{t10}</h2>;
    $[29] = t10;
    $[30] = t8;
    $[31] = t9;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t16) {
    t17 = <div className={t11}>{t12}{t13}{t16}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t14 || $[39] !== t15 || $[40] !== t17) {
    t18 = <div className={t14} style={t15}>{t17}</div>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
};